import { store } from '@/store';
import * as Sentry from '@sentry/react';
import { resetPersistSlice } from '@/store/helpers';
import { AxiosInstance } from 'axios';
import { ExtendedAxiosRequestConfig } from '@/shared/types/interfaces';
import { AppDispatch } from '@/store';
import { CustomersController } from '@/shared/endpoints/customers';

const unauthorizedStatuses = new Set([401, 403]);
const ABORT_TIMEOUT = 30 * 1000;

const handleRequest = (http: AxiosInstance) => {
    http.interceptors.request.use(config => {
        const authData = store.getState().auth.login;
        if (authData?.token) {
            config.headers['Authorization'] = `Bearer ${authData.token}`;
        }
        const extendedConfig = config;
        const abortController = new AbortController();
        if (config.signal instanceof AbortSignal) {
            config.signal.addEventListener('abort', () => {
                abortController.abort();
            });
        }
        
        extendedConfig.signal = abortController.signal;
        const abortTimeoutId = setTimeout(() => {
            abortController.abort();
        }, ABORT_TIMEOUT);

        (extendedConfig as ExtendedAxiosRequestConfig).timeoutId = abortTimeoutId;

        return extendedConfig;
    }, Promise.reject);
};

const handleResponse = (http: AxiosInstance) => {
    http.interceptors.response.use(
        response => {
            const extendedConfig = response.config as ExtendedAxiosRequestConfig;
            if (extendedConfig.timeoutId) {
                clearTimeout(extendedConfig.timeoutId);
            }
            return response;
        },
        error => {
            if (error.config && error.config.timeoutId) {
                clearTimeout(error.config.timeoutId);
            }

            if (error?.response?.data?.message?.includes('Version mismatch')) {
                const dispatch = store.dispatch as AppDispatch;
                Sentry.captureException(error);
                dispatch(CustomersController.get());
            }

            if (unauthorizedStatuses.has(error?.request?.status)) {
                resetPersistSlice('auth');
            }

            return Promise.reject(error);
        }
    );
};

export function setAxiosInterceptors(http: AxiosInstance) {
    handleRequest(http);
    handleResponse(http);
}
